<template>
    <div class="max-w-2xl mx-auto px-4 py-6 sm:px-6 lg:px-8 space-y-6">
        <div class="flex items-center justify-between">
            <button @click="goBack" class="btn btn-secondary !p-2 !rounded-full">
                <span class="material-symbols-outlined">arrow_back</span>
            </button>
            <h1 class="text-xl font-bold text-slate-800 dark:text-slate-100">
                {{ isEditing ? 'Editar Orçamento' : 'Criar Orçamento' }}
            </h1>
            <button @click="handleSubmit" :disabled="isSubmitting" class="btn btn-primary min-w-[80px]">
                <span v-if="isSubmitting" class="material-symbols-outlined animate-spin text-lg">progress_activity</span>
                <span v-else>Salvar</span>
            </button>
        </div>

        <div class="panel p-5 sm:p-6">
            <form @submit.prevent="handleSubmit" class="space-y-6">
                <div>
                    <label for="pNome" class="form-label">Nome do Projeto</label>
                    <input id="pNome" v-model="form.nome" type="text" required
                        placeholder="Ex: Reforma da Cozinha" class="form-input" />
                </div>
                <div>
                    <label for="pDesc" class="form-label">Descrição</label>
                    <input id="pDesc" v-model="form.descricao" type="text" placeholder="Objetivos do projeto"
                        class="form-input" />
                </div>
                <div>
                    <label for="pValorOrcado" class="form-label">Orçamento Total (R$)</label>
                    <input id="pValorOrcado" v-model.number="form.valorOrcado" type="number" step="0.01" required
                        placeholder="15000.00" class="form-input" />
                </div>
                <div v-if="!isEditing">
                    <label for="pEmails" class="form-label">Convidar Membros (opcional)</label>
                    <textarea id="pEmails" v-model="form.emailsMembros" rows="3"
                        placeholder="Separe os emails por vírgula ou espaço" class="form-input"></textarea>
                </div>
            </form>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import axios from 'axios';

const router = useRouter();
const route = useRoute();

const isEditing = computed(() => !!route.params.uuid);
const isSubmitting = ref(false);

const form = ref({
    nome: '',
    descricao: '',
    valorOrcado: null,
    emailsMembros: '' 
});

const API_BASE_URL = 'https://backend.controlease.com.br/api';
const authConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const fetchOrcamento = async () => {
    if (!isEditing.value) return;
    try {
        const response = await axios.get(`${API_BASE_URL}/orcamentos-projeto/${route.params.uuid}`, authConfig());
        const data = response.data;
        form.value = {
            nome: data.nome ?? '',
            descricao: data.descricao ?? '',
            valorOrcado: data.valorOrcado ?? null,
            emailsMembros: '' 
        };
    } catch {
        alert('Erro ao carregar dados do orçamento.');
        router.push('/user/orcamento-projeto');
    }
};

const handleSubmit = async () => {
    if (isSubmitting.value) return;
    isSubmitting.value = true;
    try {
        let response;
        if (isEditing.value) {
            // eslint-disable-next-line no-unused-vars
            const { emailsMembros, ...editPayload } = form.value;
            response = await axios.put(`${API_BASE_URL}/orcamentos-projeto/${route.params.uuid}`, editPayload, authConfig());
            router.push({ name: 'OrcamentoProjeto', params: { id: route.params.uuid } });
        } else {
            const payload = { ...form.value, emailsMembros: (form.value.emailsMembros || '').split(/[\s,]+/).filter(Boolean) };
            response = await axios.post(`${API_BASE_URL}/orcamentos-projeto`, payload, authConfig());
            if (response.data?.uuid) {
                 router.push({ name: 'OrcamentoProjeto', params: { id: response.data.uuid } });
            } else {
                 router.push('/user/orcamento-projeto'); 
            }
        }
    } catch (error) {
        alert(error.response?.data?.message || `Erro ao ${isEditing.value ? 'salvar alterações' : 'criar orçamento'}.`);
    } finally {
        isSubmitting.value = false;
    }
};

const goBack = () => {
    if (isEditing.value && route.params.uuid) {
        router.push({ name: 'OrcamentoProjeto', params: { id: route.params.uuid } });
    } else {
        router.push('/user/orcamento-projeto');
    }
};

onMounted(() => {
    if (isEditing.value) {
        fetchOrcamento();
    } else {
        form.value = { nome: '', descricao: '', valorOrcado: null, emailsMembros: '' }; 
    }
});
</script>

<style scoped>
.panel { @apply bg-white/70 dark:bg-brand-800/60 backdrop-blur-lg ring-1 ring-black/5 dark:ring-white/10 rounded-2xl shadow-xl shadow-slate-200/30 dark:shadow-black/20; }
.btn { @apply inline-flex items-center justify-center font-semibold py-2.5 px-5 rounded-xl transition-all duration-200 text-sm active:scale-95 disabled:opacity-60; }
.btn-primary { @apply btn bg-brand-600 hover:bg-brand-700 text-white shadow-lg shadow-brand-500/20; }
.btn-secondary { @apply btn bg-white/70 dark:bg-slate-800/50 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 ring-1 ring-black/5 dark:ring-white/10 shadow-sm; }
.form-input, .form-select { @apply w-full px-3.5 py-2.5 text-sm rounded-xl bg-white dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500; }
.form-label { @apply block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5; }
.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
</style>